/**
 * CSL records → BibTeX entries.
 *
 * The site's formatter works on CSL; this writes the same records out for LaTeX. Names
 * arrive structured (family, given, particle, suffix, or a literal for an organisation),
 * so each is written in BibTeX's unambiguous "von Last, Jr, First" form and the list is
 * joined with "and" — the only separator BibTeX reads. An organisation is wrapped in
 * braces so no style mistakes "World Health Organization" for a person named
 * Organization.
 */

import {
  type BibEntry,
  type BibField,
  MONTH_MACRO_NAMES,
  serializeEntry,
} from "@/lib/tool-kit/reference-tools/bibtex";

interface CslName {
  family?: string;
  given?: string;
  suffix?: string;
  "non-dropping-particle"?: string;
  "dropping-particle"?: string;
  literal?: string;
}

type CslRecord = Record<string, unknown>;

const TYPES: Record<string, string> = {
  "article-journal": "article",
  "article-magazine": "article",
  "article-newspaper": "article",
  book: "book",
  chapter: "incollection",
  "entry-encyclopedia": "incollection",
  "entry-dictionary": "incollection",
  "paper-conference": "inproceedings",
  report: "techreport",
  thesis: "phdthesis",
  manuscript: "unpublished",
};

const CONTAINER_FIELD: Record<string, string> = {
  article: "journal",
  incollection: "booktitle",
  inproceedings: "booktitle",
};

const ISSUER_FIELD: Record<string, string> = {
  techreport: "institution",
  phdthesis: "school",
  mastersthesis: "school",
};

const RE_SPECIAL = /[&%$#_]/g;
const RE_BRACES = /[{}]/g;
const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;
const RE_PAGE_DASH = /(?<=[\p{L}\p{N}])\s*(?:-|–|—)\s*(?=[\p{L}\p{N}])/gu;
const RE_MASTERS = /master/i;
const RE_KEY_UNSAFE = /[^a-z0-9]/g;
const RE_DIACRITICS = /\p{M}/gu;
const RE_WORDS = /[\p{L}\p{N}]+/gu;
const KEY_STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "for",
  "from",
  "in",
  "of",
  "on",
  "the",
  "to",
  "with",
]);

function text(record: CslRecord, key: string): string {
  const value = record[key];
  if (typeof value === "string") {
    return value.trim();
  }
  return typeof value === "number" ? String(value) : "";
}

function balanced(value: string): boolean {
  let depth = 0;
  for (const char of value) {
    depth += char === "{" ? 1 : 0;
    depth -= char === "}" ? 1 : 0;
    if (depth < 0) {
      return false;
    }
  }
  return depth === 0;
}

/** Escape the characters TeX treats as commands; drop braces that would not balance. */
export function escapeBibtex(value: string): string {
  const safe = balanced(value) ? value : value.replace(RE_BRACES, "");
  return safe.replace(RE_SPECIAL, (char) => `\\${char}`);
}

/** One CSL name in BibTeX's "von Last, Jr, First" form; an organisation in braces. */
export function bibtexName(name: CslName): string {
  if (name.literal) {
    return `{${escapeBibtex(name.literal)}}`;
  }
  const last = [name["non-dropping-particle"], name.family]
    .filter(Boolean)
    .join(" ");
  const first = [name.given, name["dropping-particle"]]
    .filter(Boolean)
    .join(" ");
  const parts = [last];
  if (name.suffix) {
    parts.push(name.suffix);
  }
  if (first) {
    parts.push(first);
  }
  return escapeBibtex(parts.join(", "));
}

function names(value: unknown): string {
  if (!Array.isArray(value)) {
    return "";
  }
  return (value as CslName[])
    .map(bibtexName)
    .filter((name) => name !== "")
    .join(" and ");
}

function dateParts(record: CslRecord, key: string): number[] {
  const parts = (record[key] as { "date-parts"?: unknown[][] } | undefined)?.[
    "date-parts"
  ]?.[0];
  return Array.isArray(parts) ? parts.map(Number).filter(Number.isFinite) : [];
}

function fold(value: string): string {
  return value.normalize("NFKD").replace(RE_DIACRITICS, "").toLowerCase();
}

/** surname + year + first significant title word: "hirsch2005index". */
export function citationKey(record: CslRecord): string {
  const people = (record.author ?? record.editor) as CslName[] | undefined;
  const first = Array.isArray(people) ? people[0] : undefined;
  const surname = fold(first?.family ?? first?.literal ?? "")
    .split(" ")
    .at(-1)
    ?.replace(RE_KEY_UNSAFE, "");
  const year = dateParts(record, "issued")[0];
  const word = (fold(text(record, "title")).match(RE_WORDS) ?? []).find(
    (candidate) => candidate.length >= 3 && !KEY_STOPWORDS.has(candidate)
  );
  return `${surname || "anon"}${year ?? ""}${word?.replace(RE_KEY_UNSAFE, "") ?? ""}`;
}

function bibtexType(record: CslRecord): string {
  const type = TYPES[text(record, "type")] ?? "misc";
  if (type === "phdthesis" && RE_MASTERS.test(text(record, "genre"))) {
    return "mastersthesis";
  }
  return type;
}

function braced(name: string, value: string): BibField | null {
  return value === ""
    ? null
    : { name, parts: [{ kind: "braced", text: value }], line: 0 };
}

function recordFields(record: CslRecord, type: string): (BibField | null)[] {
  const [year, month] = dateParts(record, "issued");
  const container = text(record, "container-title");
  const containerField = CONTAINER_FIELD[type];
  const issuerField = ISSUER_FIELD[type] ?? "publisher";
  const accessed = dateParts(record, "accessed");
  return [
    braced("author", names(record.author)),
    braced("editor", names(record.editor)),
    braced("title", escapeBibtex(text(record, "title"))),
    containerField ? braced(containerField, escapeBibtex(container)) : null,
    containerField || !container
      ? null
      : braced("howpublished", escapeBibtex(container)),
    braced("edition", escapeBibtex(text(record, "edition"))),
    braced("volume", escapeBibtex(text(record, "volume"))),
    braced(
      "number",
      escapeBibtex(text(record, "issue") || text(record, "number"))
    ),
    braced(
      "pages",
      escapeBibtex(text(record, "page").replace(RE_PAGE_DASH, "--"))
    ),
    year
      ? {
          name: "year",
          parts: [{ kind: "number", text: String(year) }],
          line: 0,
        }
      : null,
    month && month >= 1 && month <= 12
      ? {
          name: "month",
          parts: [{ kind: "macro", text: MONTH_MACRO_NAMES[month - 1] }],
          line: 0,
        }
      : null,
    braced(issuerField, escapeBibtex(text(record, "publisher"))),
    braced(
      "address",
      escapeBibtex(
        text(record, "publisher-place") || text(record, "event-place")
      )
    ),
    type === "phdthesis" || type === "mastersthesis"
      ? braced("type", escapeBibtex(text(record, "genre")))
      : null,
    braced("isbn", escapeBibtex(text(record, "ISBN"))),
    braced("doi", text(record, "DOI").replace(RE_DOI_PREFIX, "")),
    braced("url", text(record, "URL")),
    accessed.length === 3
      ? braced(
          "urldate",
          accessed
            .map((part, index) => String(part).padStart(index ? 2 : 4, "0"))
            .join("-")
        )
      : null,
  ];
}

function uniqueKey(base: string, used: Set<string>): string {
  let key = base;
  for (let counter = 1; used.has(key); counter++) {
    key =
      counter <= 26
        ? `${base}${String.fromCharCode(96 + counter)}`
        : `${base}${counter}`;
  }
  used.add(key);
  return key;
}

/** BibTeX entries for CSL records, with keys made unique by a, b, c… suffixes. */
export function cslToBibtexEntries(records: readonly CslRecord[]): BibEntry[] {
  const used = new Set<string>();
  return records.map((record) => {
    const key = uniqueKey(citationKey(record), used);
    const type = bibtexType(record);
    const fields = recordFields(record, type).filter(
      (field): field is BibField => field !== null
    );
    return { kind: "entry", type, key, fields, line: 0 };
  });
}

export function cslToBibtex(records: readonly CslRecord[]): string {
  const entries = cslToBibtexEntries(records);
  return entries.length === 0
    ? ""
    : `${entries.map((entry) => serializeEntry(entry)).join("\n\n")}\n`;
}
